/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useMemo} from 'react';
import {useDispatch} from 'react-redux';
import {translate} from '@ion/core/src/locale/locale';
import {Entity} from '@ion/core/src/types/entities';
import {SmartSearchCriteria} from '@ion/core/src/types/services';
import BaseSearchMapping from '@ion/features/src/search/BaseSearchMappings';
import {
  AttributeOperators,
  AttributeTypes,
  DISABLED,
  ENABLED,
  LookUpSource,
  Mappings,
  OFF,
  ON,
  SearchDateRange,
  SearchFilters,
  SearchSentinelTagType,
  SuggestionsType,
  SUSPENDED
} from '@ion/features/src/search/types';
import {useModularSearch as useGenericModularSearch} from '@ion/features/src/search/useModularSearch';
import {useSmartSearchCriteria} from '@ion/features/src/search/useSmartSearchCriteria';
import {useCurrentGlobalGroupId} from '../../hooks/useCurrentGlobalGroupId';
import {createModularSearchQueryString} from './ModularSearchUtils';
import {resetDevicesSearchQueries, updateDevicesSearchQueries} from './state';
import {SearchFields} from './types';

interface UseModularSearchResult {
  getSearchParams: (filters: SearchFilters) => void;
  mappings: BaseSearchMapping;
  filtersSearchCriteria: SmartSearchCriteria;
  isScriptVersion?: boolean;
  isVbuFwEnabled?: boolean;
}

// Generates the searchable Device attributes, options, and labels for modularized search.
const getDevicesMappings = (
  isScriptVersion?: boolean,
  isVbuFwEnabled: boolean = true
): Mappings => {
  const {
    attributes,
    date,
    status,
    vbusStatus,
    vbusMode,
    noIdReport,
    sentinelAssociation
  } = translate('common:modularsearch', {returnObjects: true});
  const {labels}  = translate('admin-devices:modularsearch', {returnObjects: true});
  const mapppingsObject = {
    [SearchFields.ANY] : {
      type       : AttributeTypes.TEXT,
      attributes : {
        value : AttributeOperators.CONTAINS,
        label : attributes?.contains
      },
      suggestion          : SuggestionsType.TEXT,
      label               : labels?.any,
      key                 : SearchFields.ANY,
      hasNoCharacterLimit : true,
      hasAnyField         : true
    },
    [SearchFields.NAME] : {
      type       : AttributeTypes.TEXT,
      attributes : {
        value : AttributeOperators.CONTAINS,
        label : attributes?.contains
      },
      suggestion          : SuggestionsType.TEXT,
      label               : labels?.name,
      key                 : SearchFields.NAME,
      hasNoCharacterLimit : true,
      hasPartialSubString : true,
      hasExactMatch       : true
    },
    [SearchFields.ESN] : {
      type       : AttributeTypes.TEXT,
      attributes : {
        value : AttributeOperators.CONTAINS,
        label : attributes?.contains
      },
      suggestion          : SuggestionsType.TEXT,
      label               : labels?.esn,
      key                 : SearchFields.ESN,
      hasNoCharacterLimit : true,
      hasPartialSubString : true,
      hasLargeMaxLimit    : !isScriptVersion,
      hasExactMatch       : true
    },
    [SearchFields.SERIAL_NUMBER] : {
      type       : AttributeTypes.TEXT,
      attributes : {
        value : AttributeOperators.CONTAINS,
        label : attributes?.contains
      },
      suggestion          : SuggestionsType.TEXT,
      label               : labels?.serialNumber,
      key                 : SearchFields.SERIAL_NUMBER,
      hasNoCharacterLimit : true,
      hasPartialSubString : true,
      hasLargeMaxLimit    : !isScriptVersion,
      hasExactMatch       : true
    },
    [SearchFields.IMEI] : {
      type       : AttributeTypes.TEXT,
      attributes : {
        value : AttributeOperators.CONTAINS,
        label : attributes?.contains
      },
      suggestion          : SuggestionsType.TEXT,
      label               : labels?.imei,
      key                 : SearchFields.IMEI,
      hasNoCharacterLimit : true,
      hasPartialSubString : true,
      hasExactMatch       : true
    },
    [SearchFields.IMEI2] : {
      type       : AttributeTypes.TEXT,
      attributes : {
        value : AttributeOperators.CONTAINS,
        label : attributes?.contains
      },
      suggestion          : SuggestionsType.TEXT,
      label               : labels?.imei2,
      key                 : SearchFields.IMEI,
      hasNoCharacterLimit : true,
      hasPartialSubString : true,
      hasExactMatch       : true
    },
    [SearchFields.TYPE] : {
      type       : AttributeTypes.LOOKUP,
      attributes : {
        value : AttributeOperators.IS,
        label : attributes?.is
      },
      suggestion        : SuggestionsType.LOOKUP,
      label             : labels?.type,
      key               : SearchFields.TYPE,
      suggestionsSource : LookUpSource.DeviceType
    },
    [SearchFields.STATUS] : {
      type       : AttributeTypes.SELECT,
      attributes : {
        value : AttributeOperators.IN,
        label : attributes?.in
      },
      options : [
        {
          label : status?.Enabled,
          value : ENABLED
        },
        {
          label : status?.Disabled,
          value : DISABLED
        },
        {
          label : status?.Suspended,
          value : SUSPENDED
        }
      ],
      suggestion : SuggestionsType.SELECTION,
      label      : labels?.status,
      key        : SearchFields.STATUS
    },
    [SearchFields.ANDROID_OS_VERSION] : {
      type       : AttributeTypes.TEXT,
      attributes : {
        value : AttributeOperators.CONTAINS,
        label : attributes?.contains
      },
      suggestion          : SuggestionsType.TEXT,
      label               : labels?.androidOSVersion,
      key                 : SearchFields.ANDROID_OS_VERSION,
      hasNoCharacterLimit : true,
      hasPartialSubString : true,
      hasExactMatch       : true
    }
  };
  mapppingsObject[SearchFields.SENTINEL_ASSOCIATION] = {
    type       : AttributeTypes.SELECT,
    attributes : {
      value : AttributeOperators.IN,
      label : attributes?.in
    },
    options : [
      {
        label : sentinelAssociation?.primary,
        value : SearchSentinelTagType.PRIMARY
      },
      {
        label : sentinelAssociation?.secondary,
        value : SearchSentinelTagType.SECONDARY
      },
      {
        label : sentinelAssociation?.all,
        value : SearchSentinelTagType.ALL
      }
    ],
    suggestion : SuggestionsType.SELECTION,
    label      : labels?.sentinelAssociation,
    key        : SearchFields.SENTINEL_ASSOCIATION
  };
  if (isScriptVersion) {
    mapppingsObject[SearchFields.ANY] = {
      type       : AttributeTypes.TEXT,
      attributes : {
        value : AttributeOperators.CONTAINS,
        label : attributes?.contains
      },
      suggestion          : SuggestionsType.TEXT,
      label               : labels?.any,
      key                 : SearchFields.ANY,
      hasNoCharacterLimit : false,
      hasPartialSubString : true
    };
    mapppingsObject[SearchFields.ASSET_NAME] = {
      type       : AttributeTypes.TEXT,
      attributes : {
        value : AttributeOperators.CONTAINS,
        label : attributes?.contains
      },
      suggestion          : SuggestionsType.TEXT,
      label               : labels?.assetName,
      key                 : SearchFields.ASSET_NAME,
      hasNoCharacterLimit : true,
      hasPartialSubString : true
    };
    mapppingsObject[SearchFields.ACCOUNT_NAME] = {
      type       : AttributeTypes.TEXT,
      attributes : {
        value : AttributeOperators.CONTAINS,
        label : attributes?.contains
      },
      suggestion          : SuggestionsType.TEXT,
      label               : labels?.accountName,
      key                 : SearchFields.ACCOUNT_NAME,
      hasNoCharacterLimit : true,
      hasPartialSubString : true
    };
    mapppingsObject[SearchFields.KERNEL_VERSION] = {
      type       : AttributeTypes.TEXT,
      attributes : {
        value : AttributeOperators.CONTAINS,
        label : attributes?.contains
      },
      suggestion          : SuggestionsType.TEXT,
      label               : labels?.kernelVersion,
      key                 : SearchFields.KERNEL_VERSION,
      hasNoCharacterLimit : true,
      hasPartialSubString : true
    };
  } else {
    mapppingsObject[SearchFields.ID] = {
      type       : AttributeTypes.TEXT,
      attributes : {
        value : AttributeOperators.IS,
        label : attributes?.is
      },
      suggestion          : SuggestionsType.TEXT,
      label               : labels?.id,
      key                 : SearchFields.ID,
      hasNoCharacterLimit : true,
      hasNumericValue     : true
    };
    mapppingsObject[SearchFields.AIR_ID] = {
      type       : AttributeTypes.TEXT,
      attributes : {
        value : AttributeOperators.CONTAINS,
        label : attributes?.contains
      },
      suggestion          : SuggestionsType.TEXT,
      label               : labels?.airId,
      key                 : SearchFields.AIR_ID,
      hasNoCharacterLimit : true,
      hasPartialSubString : true,
      hasExactMatch       : true
    };
    mapppingsObject[SearchFields.ICCID] = {
      type       : AttributeTypes.TEXT,
      attributes : {
        value : AttributeOperators.CONTAINS,
        label : attributes?.contains
      },
      suggestion          : SuggestionsType.TEXT,
      label               : labels?.iccid,
      key                 : SearchFields.ICCID,
      hasNoCharacterLimit : true,
      hasPartialSubString : true,
      hasExactMatch       : true
    };
    mapppingsObject[SearchFields.MEID] = {
      type       : AttributeTypes.TEXT,
      attributes : {
        value : AttributeOperators.CONTAINS,
        label : attributes?.contains
      },
      suggestion          : SuggestionsType.TEXT,
      label               : labels?.meid,
      key                 : SearchFields.MEID,
      hasNoCharacterLimit : true,
      hasPartialSubString : true,
      hasExactMatch       : true
    };
    mapppingsObject[SearchFields.APP_ID] = {
      type       : AttributeTypes.TEXT,
      attributes : {
        value : AttributeOperators.CONTAINS,
        label : attributes?.contains
      },
      suggestion              : SuggestionsType.TEXT,
      label                   : labels?.appId,
      key                     : SearchFields.APP_ID,
      hasNoCharacterLimit     : true,
      hasPartialSubString     : true,
      hasNumericValue         : true,
      hasExactMatch           : true,
      hasMultipleNumericValue : true
    };
    mapppingsObject[SearchFields.CATEGORY] = {
      type       : AttributeTypes.SELECT,
      attributes : {
        value : AttributeOperators.IS,
        label : attributes?.IS
      },
      suggestion        : SuggestionsType.LOOKUP,
      label             : labels?.category,
      key               : SearchFields.CATEGORY,
      suggestionsSource : LookUpSource.Categories
    };
    mapppingsObject[SearchFields.ARCHITECTURE] = {
      label             : labels?.architecture,
      key               : SearchFields.ARCHITECTURE,
      type              : AttributeTypes.SELECT,
      suggestion        : SuggestionsType.LOOKUP,
      suggestionsSource : LookUpSource.Architectures,
      attributes        : {
        value : AttributeOperators.IS,
        label : attributes?.IS
      }
    };
    mapppingsObject[SearchFields.FIRMWARE_VERSION] = {
      type       : AttributeTypes.TEXT,
      attributes : {
        value : AttributeOperators.CONTAINS,
        label : attributes?.contains
      },
      suggestion          : SuggestionsType.TEXT,
      label               : labels?.firmwareVersion,
      key                 : SearchFields.FIRMWARE_VERSION,
      hasNoCharacterLimit : true,
      hasPartialSubString : true,
      hasExactMatch       : true
    };
    mapppingsObject[SearchFields.CONFIG_VERSION] = {
      type       : AttributeTypes.TEXT,
      attributes : {
        value : AttributeOperators.CONTAINS,
        label : attributes?.contains
      },
      suggestion          : SuggestionsType.TEXT,
      label               : labels?.configVersion,
      key                 : SearchFields.CONFIG_VERSION,
      hasNoCharacterLimit : true,
      hasPartialSubString : true,
      hasExactMatch       : true
    };
    mapppingsObject[SearchFields.ID_REPORT_TIMESTAMP] = {
      type       : AttributeTypes.DATE,
      attributes : {
        value : AttributeOperators.BETWEEN,
        label : attributes?.between
      },
      options : [
        {
          label : noIdReport?.label,
          value : SearchDateRange.NULL
        },
        {
          label : date?.lastDay,
          value : SearchDateRange.ONE
        },
        {
          label : date?.lastThreeDays,
          value : SearchDateRange.THREE
        },
        {
          label : date?.lastSevenDays,
          value : SearchDateRange.SEVEN
        }
      ],
      suggestion : SuggestionsType.DATE,
      label      : labels?.idReportTimestamp,
      key        : SearchFields.ID_REPORT_TIMESTAMP
    };
    mapppingsObject[SearchFields.LAST_REPORT_DATE] = {
      type       : AttributeTypes.DATE,
      attributes : {
        value : AttributeOperators.BETWEEN,
        label : attributes?.between
      },
      options : [
        {
          label : date?.lastDay,
          value : SearchDateRange.ONE
        },
        {
          label : date?.lastThreeDays,
          value : SearchDateRange.THREE
        },
        {
          label : date?.lastSevenDays,
          value : SearchDateRange.SEVEN
        }
      ],
      suggestion : SuggestionsType.DATE,
      label      : labels?.lastReportDate,
      key        : SearchFields.LAST_REPORT_DATE
    };
    mapppingsObject[SearchFields.GROUPS] = {
      type       : AttributeTypes.SELECT,
      attributes : {
        value : AttributeOperators.IS,
        label : attributes?.is
      },
      suggestion        : SuggestionsType.LOOKUP,
      label             : labels?.groups,
      key               : SearchFields.GROUPS,
      suggestionsSource : LookUpSource.Group
    };
    mapppingsObject[SearchFields.VBUS_STATUS] = {
      type       : AttributeTypes.SELECT,
      attributes : {
        value : AttributeOperators.IN,
        label : attributes?.in
      },
      options : [
        {
          label : vbusStatus?.on,
          value : ON
        },
        {
          label : vbusStatus?.off,
          value : OFF
        }
      ],
      suggestion : SuggestionsType.SELECTION,
      label      : labels?.vbusStatus,
      key        : SearchFields.VBUS_STATUS
    };
    mapppingsObject[SearchFields.MDT] = {
      type       : AttributeTypes.TEXT,
      attributes : {
        value : AttributeOperators.CONTAINS,
        label : attributes?.contains
      },
      suggestion          : SuggestionsType.TEXT,
      label               : labels?.mdt,
      key                 : SearchFields.MDT,
      hasNoCharacterLimit : true,
      hasPartialSubString : true,
      hasExactMatch       : true
    };
    mapppingsObject[SearchFields.LMU] = {
      type       : AttributeTypes.TEXT,
      attributes : {
        value : AttributeOperators.CONTAINS,
        label : attributes?.contains
      },
      suggestion          : SuggestionsType.TEXT,
      label               : labels?.lmu,
      key                 : SearchFields.LMU,
      hasNoCharacterLimit : true,
      hasPartialSubString : true,
      hasExactMatch       : true
    };
    mapppingsObject[SearchFields.USER_ESN_MOBILE_ID] = {
      type       : AttributeTypes.TEXT,
      attributes : {
        value : AttributeOperators.CONTAINS,
        label : attributes?.contains
      },
      suggestion          : SuggestionsType.TEXT,
      label               : labels?.userEsnMobileId,
      key                 : SearchFields.USER_ESN_MOBILE_ID,
      hasNoCharacterLimit : true,
      hasPartialSubString : true,
      hasExactMatch       : true
    };
    mapppingsObject[SearchFields.VBUS_MODE] = {
      type       : AttributeTypes.SELECT,
      attributes : {
        value : AttributeOperators.IN,
        label : attributes?.in
      },
      options : [
        {
          label : vbusMode?.OBD,
          value : vbusMode?.OBD
        },
        {
          label : vbusMode?.JPOD,
          value : vbusMode?.JPOD
        }
      ],
      suggestion : SuggestionsType.SELECTION,
      label      : labels?.vbusMode,
      key        : SearchFields.VBUS_MODE
    };
    if (!isVbuFwEnabled) {
      mapppingsObject[SearchFields.VBUS_MODE].options.push({
        label : vbusMode?.VBU,
        value : vbusMode?.VBU
      });
    }
    mapppingsObject[SearchFields.JPOD_FIRMWARE] = {
      type       : AttributeTypes.TEXT,
      attributes : {
        value : AttributeOperators.IS,
        label : attributes?.is
      },
      suggestion          : SuggestionsType.TEXT,
      label               : isVbuFwEnabled ? labels?.jpodFirmware : labels?.vbuFirmware,
      key                 : SearchFields.JPOD_FIRMWARE,
      hasNoCharacterLimit : true,
      hasPartialSubString : true
    };
    mapppingsObject[SearchFields.JPOD_CONFIG] = {
      type       : AttributeTypes.TEXT,
      attributes : {
        value : AttributeOperators.IS,
        label : attributes?.is
      },
      suggestion          : SuggestionsType.TEXT,
      label               : labels?.jpodConfig,
      key                 : SearchFields.JPOD_CONFIG,
      hasNoCharacterLimit : true,
      hasPartialSubString : true
    };
    mapppingsObject[SearchFields.ANDROID_MDT_FIRMWARE] = {
      type       : AttributeTypes.TEXT,
      attributes : {
        value : AttributeOperators.IS,
        label : attributes?.is
      },
      suggestion          : SuggestionsType.TEXT,
      label               : labels?.androidMdtFirmware,
      key                 : SearchFields.ANDROID_MDT_FIRMWARE,
      hasNoCharacterLimit : true,
      hasPartialSubString : true
    };
    mapppingsObject[SearchFields.BLE3_FIRWARE] = {
      type       : AttributeTypes.TEXT,
      attributes : {
        value : AttributeOperators.IS,
        label : attributes?.is
      },
      suggestion          : SuggestionsType.TEXT,
      label               : labels?.ble3Firmware,
      key                 : SearchFields.BLE3_FIRWARE,
      hasNoCharacterLimit : true,
      hasPartialSubString : true
    };
    if (isVbuFwEnabled) {
      mapppingsObject[SearchFields.VBU3_FIRWARE] = {
        type       : AttributeTypes.TEXT,
        attributes : {
          value : AttributeOperators.IS,
          label : attributes?.is
        },
        suggestion          : SuggestionsType.TEXT,
        label               : labels?.vbu3Firmware,
        key                 : SearchFields.VBU3_FIRWARE,
        hasNoCharacterLimit : true,
        hasPartialSubString : true
      };
    }
    mapppingsObject[SearchFields.VBUS_DATABASE] = {
      type       : AttributeTypes.TEXT,
      attributes : {
        value : AttributeOperators.IS,
        label : attributes?.is
      },
      suggestion          : SuggestionsType.TEXT,
      label               : labels?.vbusDatabase,
      key                 : SearchFields.VBUS_DATABASE,
      hasNoCharacterLimit : true,
      hasPartialSubString : true
    };
    mapppingsObject[SearchFields.RADIO_FIRMWARE] = {
      type       : AttributeTypes.TEXT,
      attributes : {
        value : AttributeOperators.IS,
        label : attributes?.is
      },
      suggestion          : SuggestionsType.TEXT,
      label               : labels?.radioFirmware,
      key                 : SearchFields.RADIO_FIRMWARE,
      hasNoCharacterLimit : true,
      hasPartialSubString : true
    };
  }
  
  const firstItems = [SearchFields.ANY, SearchFields.NAME];

  const compare = ([keyA, valueA], [keyB, valueB]) => {
    const labelA = valueA.label;
    const labelB = valueB.label;

    // Check if both items are in the firstItems array
    if (firstItems.includes(keyA) && firstItems.includes(keyB)) {
      // Maintain the original order of the items in firstItems array
      return firstItems.indexOf(keyA) - firstItems.indexOf(keyB);
    }

    // Check if only one item is in the firstItems array
    if (firstItems.includes(keyA)) {
      // Place item from firstItems before other items
      return -1;
    }

    if (firstItems.includes(keyB)) {
      // Place item from firstItems before other items
      return 1;
    }

    // Sort the rest of the items based on label
    return labelA?.localeCompare(labelB);
  };

  const sortedProperties = Object.entries(mapppingsObject)
    .sort(compare)
    .map(([key, value]) => [key, value]);

  // Merge sorted properties and ANY property into final object
  return Object.fromEntries(sortedProperties);
};

/**
 * Custom hook for using modular search in the Devices module.
 */
export const useModularSearch = ({
  onRefresh,
  viewSubGroups = false,
  isScriptVersion = true,
  isVbuFwEnabled = true
}): UseModularSearchResult => {
  const dispatch       = useDispatch();
  const onReset        = useCallback(() => dispatch(resetDevicesSearchQueries()), []);
  const onUpdate       = useCallback((filters: SearchFilters) => dispatch(updateDevicesSearchQueries(filters)), []);
  const globalGroupId  = useCurrentGlobalGroupId();
  const entityMappings = useMemo(() => getDevicesMappings(isScriptVersion, isVbuFwEnabled), []);
  const {
    getSearchParams,
    mappings,
    filters
  } = useGenericModularSearch({
    onReset,
    onUpdate,
    entityMappings,
    onRefresh,
    globalGroupId,
    viewSubGroups
  });
  
  const filtersSearchCriteria = useSmartSearchCriteria({
    entity      : Entity.device,
    queryString : createModularSearchQueryString(filters),
    globalGroupId,
    viewSubGroups
  });
  return {getSearchParams, mappings, filtersSearchCriteria};
};

export default useModularSearch;
